import React, { ReactNode } from 'react'
import DefaultVocab from './vocabs/default'

export type VocabType = {
  /** flexible mapping of object name to brand-dependant vocab string e.g. ADD_TO_TROLLEY : 'Add to trolley' */
  [key: string]: string
}

export type ProviderProps = {
  /** React children element */
  children: ReactNode
  /** This is the vocab object containing brand-dependant vocab */
  vocab: VocabType
}

/**
 * Context used to manage and provide vocabulary data.
 * Default context is the default vocabulary object.
 */
export const VocabContext = React.createContext<VocabType>(DefaultVocab)

/**
 * Provider function provides the vocabulary context to its children.
 * @param children - The children elements that receive the vocab context.
 * @param vocab - The brand dependent vocab object
 * @returns a vocab context provider that wraps its children.
 */
export const VocabProvider = ({ children, vocab }: ProviderProps) => {
  if (!children) {
    return null
  }

  return <VocabContext.Provider value={vocab}>{children}</VocabContext.Provider>
}
/**
 * Retrieves the vocabulary context using React's useContext hook.
 * @returns The context containing the vocabulary.
 */
export const useVocab = () => {
  return React.useContext(VocabContext)
}

/**
 * Consumes the vocabulary context and provides it to its children.
 * @param children - The children elements that receive the vocab context.
 * @returns The children elements with vocabulary context.
 * @throws {Error} Throws an error if used outside of VocabProvider.
 */
export const VocabConsumer = VocabContext.Consumer

export default VocabProvider
